import { useEffect, useRef } from "react";

import { SectionProgressIndicator } from "./SectionProgressIndicator";
import { Row } from "@/components";
import { Button } from "@/components/ui/button";

import { fireMiniConfetti } from "@/lib/confetti";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

const CONFETTI_DELAY_MS = 850;

export function TableOfContentsButton<T>({
  activeTab,
  index,
  isDataSettled,
  label,
  progress,
  screenName,
  setActiveTab,
  tabValue,
}: {
  activeTab: T;
  index: number;
  isDataSettled: boolean;
  label: string;
  progress: { filled: number; total: number; graceNote?: string };
  screenName: MIXPANEL_SCREENS;
  setActiveTab: (tab: T) => void;
  tabValue: T;
}) {
  const isActive = activeTab === tabValue;

  const isComplete = progress.total > 0 && progress.filled >= progress.total;

  // Logic below is for confetti cannon animation
  const buttonRef = useRef<HTMLButtonElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const hasSettledRef = useRef(false);
  const prevCompleteRef = useRef(false);

  // Animate the button and fire confetti when the section is completed.
  // We wait until all query data has settled before tracking transitions,
  // so that the initial data load doesn't trigger a false positive.
  useEffect(() => {
    if (!isDataSettled) return;

    if (!hasSettledRef.current) {
      hasSettledRef.current = true;
      prevCompleteRef.current = isComplete;
      return;
    }
    if (isComplete && !prevCompleteRef.current) {
      const buttonEl = buttonRef.current;
      if (buttonEl) {
        buttonEl.style.transformOrigin = "left center";
        buttonEl.style.transition = "transform 0.3s ease-out";
        buttonEl.style.transform = "rotate(-3deg)";
        setTimeout(() => {
          buttonEl.style.transition = "transform 0.25s ease-in";
          buttonEl.style.transform = "rotate(-3deg) scaleX(0.93)";
        }, 300);
        setTimeout(() => {
          buttonEl.style.transition = "transform 0.4s ease-out";
          buttonEl.style.transform = "rotate(0deg) scaleX(1)";
        }, CONFETTI_DELAY_MS);
        setTimeout(() => {
          buttonEl.style.transformOrigin = "";
        }, 1250);
      }

      const indicatorEl = indicatorRef.current;
      const confettiTimer = setTimeout(() => {
        if (indicatorEl) {
          fireMiniConfetti(indicatorEl);
        }
      }, CONFETTI_DELAY_MS);

      prevCompleteRef.current = true;
      return () => {
        clearTimeout(confettiTimer);
      };
    }
    prevCompleteRef.current = isComplete;
  }, [isComplete, isDataSettled]);

  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="ghost"
      className={cn(
        "w-full justify-start gap-3 rounded-lg px-3 py-3 text-base font-normal text-slate-600",
        "hover:bg-accent",
        isActive &&
          "bg-primary-100 font-medium text-primary-800 hover:bg-primary-50 hover:text-primary-700",
      )}
      onClick={() => {
        if (activeTab !== tabValue) {
          mixpanelUtils.trackTabView(screenName, label);
        }
        setActiveTab(tabValue);
      }}
    >
      <Row className="flex-1 items-center gap-3">
        <span className="shrink-0 text-base">{index}.</span>
        <span className="truncate">{label}</span>
      </Row>
      <span ref={indicatorRef}>
        <SectionProgressIndicator {...progress} />
      </span>
    </Button>
  );
}
